/**
 * Plugging in the local AI.
 *
 * In the desktop app: find the agents installed on this machine, sign in to one's own account
 * (in the agent's own sign-in page, behind the app's sign-in screen — the app never sees the
 * password), optionally keep provider API keys in the credential
 * store, and switch on the runner — this machine then takes the work sent to the chosen
 * repositories and does each step with a fresh agent. In a browser there is no local AI to plug
 * in, so the page says how work reaches an agent from there instead.
 *
 * It is the "From the desktop app" tab of Agentes › Conectar agente (pages/ConnectAgent.tsx).
 */

import { AgentAccountsHint } from '../components/AgentAccountsHint'
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bot, Check, KeyRound, Loader2, LogIn, Power, RefreshCw, TerminalSquare, Trash2, UserPlus } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { autostartEnabled, autostartSet, isDesktop, vaultHas } from '../lib/desktop'
import { AGENT_NAMES, agentAccountAdd, agentAccountForget, agentLogin, aiKeyClear, aiKeyHas, aiKeyStore, detectAgents, type AiProvider, type DetectedAgent, type LocalAgentId, type Permission } from '../lib/localAgent'
import { agentAuthStatus, hasInAppLogin } from '../lib/agentLogin'
import { AUTOSTART_CHOSEN, issueKey, keyRefused } from '../lib/desktopAutoRunner'
import { folderFor } from '../lib/repoFolders'
import { inventoryChanged } from '../lib/machineInventory'
import { readRunnerSettings, saveRunnerSettings, useRunnerStatus } from '../lib/runnerHost'
import type { RunnerSettings } from '../lib/localRunner'
import { Badge, ErrorBanner, Panel } from '../components/primitives'
import { AgentMatrix } from '../components/AgentMatrix'
import { AgentLoginDialog } from '../components/AgentLoginDialog'
import { TelegramPanel } from '../components/TelegramPanel'
import { FailoverOrder } from '../components/FailoverOrder'
import { LocalAiSetup } from '../components/LocalAiSetup'

import { DOWNLOAD_URL } from '../lib/startPath'

function autostartChosen(): boolean {
  try {
    return localStorage.getItem(AUTOSTART_CHOSEN) === '1'
  } catch {
    return true
  }
}

const PROVIDERS: Array<{ id: AiProvider; label: string }> = [
  { id: 'anthropic', label: 'Anthropic (Claude)' },
  { id: 'openai', label: 'OpenAI' },
  { id: 'gemini', label: 'Google Gemini' },
]

export default function ConnectAi() {
  const { t } = useI18n()
  if (!isDesktop()) return <InBrowser />

  return (
    <div className="stack">
      <p className="muted small" style={{ maxWidth: 760, margin: 0 }}>{t('connect.intro')}</p>
      <Agents />
      <DevPulseKey />
      <ProviderKeys />
      <Runner />
      <ClaudeOnlyTelegram />
      <Panel title={t('matrix.title')}>
        <AgentMatrix />
        <LocalAiSetup />
      </Panel>
    </div>
  )
}

function InBrowser() {
  const { t } = useI18n()
  return (
    <Panel title={t('connect.browserTitle')}>
      <div className="stack">
        <p className="small" style={{ margin: 0 }}>{t('connect.browserBody')}</p>
        <div className="row row--tight row--wrap">
          <a className="btn btn--primary" href={DOWNLOAD_URL} target="_blank" rel="noreferrer">{t('connect.browserDownload')}</a>
          {/* The other card of the same screen: connecting from an IDE works without the app, with less. */}
          <Link className="btn" to="/agents/connect?tab=ide">{t('connect.browserSetup')}</Link>
        </div>
        <h3 className="small" style={{ margin: 'var(--s3) 0 0' }}>{t('matrix.title')}</h3>
        <AgentMatrix />
      </div>
    </Panel>
  )
}

/** Telegram is set up from here, and only when Claude Code is on this machine. */
function ClaudeOnlyTelegram() {
  const { data: agents } = useQuery({ queryKey: ['local-agents'], queryFn: detectAgents, staleTime: 60_000 })
  const claude = agents?.find((agent) => agent.id === 'claude')
  return claude?.installed ? <TelegramPanel /> : null
}

function Agents() {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const { data, isLoading, refetch, isFetching } = useQuery({ queryKey: ['local-agents'], queryFn: detectAgents, staleTime: 60_000 })
  const [problem, setProblem] = useState<string | null>(null)
  const [signingIn, setSigningIn] = useState<DetectedAgent | null>(null)

  const login = async (agent: DetectedAgent) => {
    setProblem(null)
    // Claude Code and Codex sign in behind the app's own screen; the others still get a terminal
    // with their own sign-in, which they need.
    if (hasInAppLogin(agent.id)) {
      setSigningIn(agent)
      return
    }
    try {
      await agentLogin(agent.id)
    } catch (error) {
      // Linux without any terminal app we know: say so, with the command to run by hand.
      const text = String(error)
      setProblem(text.startsWith('no-terminal:') ? t('connect.noTerminal', { command: text.slice('no-terminal:'.length) }) : t('connect.loginFailed'))
    }
  }

  // The second Claude account (docs/refactor/60): its own folder first, then the same sign-in screen as the first's.
  const addSecond = async () => {
    setProblem(null)
    try {
      await agentAccountAdd('claude-2')
      await refetch()
      setSigningIn({ id: 'claude-2', name: AGENT_NAMES['claude-2']!, installed: true, runnable: true, version: null })
    } catch {
      setProblem(t('connect.loginFailed'))
    }
  }
  const forgetSecond = async () => {
    if (!window.confirm(t('connect.forgetAccountConfirm'))) return
    await agentAccountForget('claude-2').catch(() => undefined)
    void queryClient.invalidateQueries({ queryKey: ['agent-auth'] })
    await refetch()
  }

  const closeLogin = useCallback(() => setSigningIn(null), [])
  const signedIn = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['agent-auth'] })
    void queryClient.invalidateQueries({ queryKey: ['local-agents'] })
  }, [queryClient])

  const detect = () => {
    void refetch()
    void queryClient.invalidateQueries({ queryKey: ['agent-auth'] })
  }

  return (
    <Panel
      title={t('connect.agentsTitle')}
      actions={
        <button type="button" className="btn btn--sm btn--ghost" onClick={detect} disabled={isFetching}>
          <RefreshCw size={13} /> {t('connect.detect')}
        </button>
      }
    >
      {/* Finding them takes a few seconds: said, not an empty box (51, D5). */}
      {isLoading && (
        <p className="small muted row row--tight" role="status" style={{ margin: 0 }}>
          <Loader2 size={14} className="spin" aria-hidden /> {t('connect.searchingAgents')}
        </p>
      )}
      {problem && <ErrorBanner message={problem} />}
      {/* Fewer than two agents ready: how to create, install and sign in to Claude and Codex (docs/refactor/22). */}
      {data && <AgentAccountsHint agents={data} onSignIn={(agent) => void login(agent)} />}
      <div className="repo-list">
        {/* The second Claude account is listed once connected (docs/refactor/60); until then, the offer sits under the first. */}
        {(data ?? []).filter((agent) => agent.installed || agent.id !== 'claude-2').map((agent) => (
          <div key={agent.id} className="repo-row">
            <Bot size={18} className="repo-row__icon" />
            <div className="stack" style={{ gap: 2, flex: 1, minWidth: 0 }}>
              <div className="row row--tight row--wrap">
                <strong>{agent.name}</strong>
                {agent.installed ? <Badge tone="ok">{t('connect.installed')}</Badge> : <Badge tone="neutral">{t('connect.notInstalled')}</Badge>}
                {agent.installed && !agent.runnable && <Badge tone="info">{t('connect.detectOnly')}</Badge>}
                {agent.installed && <SignedInBadge agent={agent.id} />}
              </div>
              {agent.version && <span className="tiny muted mono truncate">{agent.version}</span>}
              {agent.id === 'claude-2' && <span className="tiny muted">{t('connect.secondAccountHint')}</span>}
              {agent.id === 'claude' && agent.installed && !data?.some((a) => a.id === 'claude-2' && a.installed) && (
                <span className="row row--tight row--wrap tiny muted">
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => void addSecond()}>
                    <UserPlus size={13} /> {t('connect.anotherClaude')}
                  </button>
                  {t('connect.anotherClaudeHint')}
                </span>
              )}
            </div>
            {agent.installed && <SignInButton agent={agent.id} onClick={() => void login(agent)} />}
            {agent.id === 'claude-2' && agent.installed && (
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => void forgetSecond()} title={t('connect.forgetAccountHint')}>
                <Trash2 size={13} /> {t('connect.forgetAccount')}
              </button>
            )}
          </div>
        ))}
      </div>
      <p className="tiny muted" style={{ marginBottom: 0 }}>{t('agentLogin.hint')}</p>
      {signingIn && <AgentLoginDialog agent={signingIn} onClose={closeLogin} onSignedIn={signedIn} />}
    </Panel>
  )
}

/** Whether the agent says it is signed in: true, false, or undefined when it cannot say. */
function useAgentSignedIn(agent: LocalAgentId) {
  return useQuery({ queryKey: ['agent-auth', agent], queryFn: () => agentAuthStatus(agent), staleTime: 60_000 }).data
}

/**
 * "Entrar na conta" until the agent is signed in; then a quieter "Trocar de conta", so a connected
 * agent does not look like it still needs signing in, while another account stays one click away.
 */
export function SignInButton({ agent, onClick }: { agent: LocalAgentId; onClick: () => void }) {
  const { t } = useI18n()
  const signedIn = useAgentSignedIn(agent) === true
  return (
    <button
      type="button"
      className={signedIn ? 'btn btn--sm btn--ghost' : 'btn btn--sm'}
      onClick={onClick}
      title={hasInAppLogin(agent) ? t('agentLogin.hint') : t('connect.signInHint')}
    >
      {signedIn ? <><RefreshCw size={13} /> {t('agentLogin.switchAccount')}</> : <><LogIn size={13} /> {t('connect.signIn')}</>}
    </button>
  )
}

/** "Connected" or "Not connected" beside an agent, when the agent can say; nothing when it cannot. */
function SignedInBadge({ agent }: { agent: LocalAgentId }) {
  const { t } = useI18n()
  const data = useAgentSignedIn(agent)
  if (data === true) return <Badge tone="ok"><Check size={11} /> {t('agentLogin.connected')}</Badge>
  if (data === false) return <Badge tone="warn">{t('agentLogin.notConnected')}</Badge>
  return null
}

/** The DevPulse agent key the runner asks for work with — issued for oneself and kept in the vault. */
function DevPulseKey() {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const { data: has } = useQuery({ queryKey: ['vault-has'], queryFn: vaultHas })
  // A key in the vault is not a working key: the runner hears the server refuse it.
  const refused = keyRefused(useRunnerStatus())
  const issue = useMutation({
    mutationFn: async () => {
      await issueKey()
      saveRunnerSettings(readRunnerSettings())
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['vault-has'] }),
  })

  return (
    <Panel title={t('connect.keyTitle')}>
      <div className="row row--between row--wrap">
        <span className="small">{refused ? t('connect.keyRefused') : has ? t('connect.keyStored') : t('connect.keyMissing')}</span>
        {has && !refused ? (
          <Badge tone="ok"><Check size={11} /> {t('connect.keyOk')}</Badge>
        ) : (
          <button type="button" className="btn btn--sm btn--primary" onClick={() => issue.mutate()} disabled={issue.isPending}>
            <KeyRound size={13} /> {t('connect.keyIssue')}
          </button>
        )}
      </div>
      {issue.isError && <ErrorBanner message={String(issue.error).startsWith('no-secret-service') ? t('connect.noKeyring') : t('error.generic')} />}
    </Panel>
  )
}

function ProviderKeys() {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const { data: stored } = useQuery({
    queryKey: ['ai-keys'],
    queryFn: async () => Object.fromEntries(await Promise.all(PROVIDERS.map(async (p) => [p.id, await aiKeyHas(p.id)] as const))) as Record<AiProvider, boolean>,
  })
  const [drafts, setDrafts] = useState<Record<string, string>>({})

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['ai-keys'] })

  return (
    <Panel title={t('connect.providersTitle')}>
      <p className="tiny muted" style={{ marginTop: 0 }}>{t('connect.providersHint')}</p>
      <div className="stack">
        {PROVIDERS.map((provider) => (
          <div key={provider.id} className="row row--wrap">
            <span className="small" style={{ width: 170 }}>{provider.label}</span>
            {stored?.[provider.id] ? (
              <>
                <Badge tone="ok"><Check size={11} /> {t('connect.keyOk')}</Badge>
                <button type="button" className="btn btn--sm btn--ghost" onClick={() => void aiKeyClear(provider.id).then(refresh)}>
                  {t('connect.forget')}
                </button>
              </>
            ) : (
              <>
                <input
                  className="input mono"
                  type="password"
                  autoComplete="off"
                  style={{ flex: 1, minWidth: 200 }}
                  value={drafts[provider.id] ?? ''}
                  onChange={(e) => setDrafts({ ...drafts, [provider.id]: e.target.value })}
                  placeholder={t('connect.pasteKey')}
                  aria-label={provider.label}
                />
                <button
                  type="button"
                  className="btn btn--sm"
                  disabled={!drafts[provider.id]?.trim()}
                  onClick={() =>
                    void aiKeyStore(provider.id, drafts[provider.id]!).then(() => {
                      setDrafts({ ...drafts, [provider.id]: '' })
                      refresh()
                    })
                  }
                >
                  {t('connect.saveKey')}
                </button>
              </>
            )}
          </div>
        ))}
      </div>
    </Panel>
  )
}

function Runner() {
  const { t } = useI18n()
  const [settings, setSettings] = useState<RunnerSettings>(readRunnerSettings)
  const status = useRunnerStatus()
  const { data: repositories } = useQuery({ queryKey: ['repositories'], queryFn: api.repositories.list })
  const { data: agents } = useQuery({ queryKey: ['local-agents'], queryFn: detectAgents, staleTime: 60_000 })
  const withFolder = (repositories ?? []).filter((r) => r.linked && folderFor(r.name))
  const runnable = (agents ?? []).filter((a) => a.installed && a.runnable)

  // Keeps the chosen agent one that can run here.
  useEffect(() => {
    if (runnable.length > 0 && !runnable.some((a) => a.id === settings.agent)) setSettings((s) => ({ ...s, agent: runnable[0]!.id }))
  }, [runnable, settings.agent])

  const update = (patch: Partial<RunnerSettings>) => {
    const next = { ...settings, ...patch }
    setSettings(next)
    saveRunnerSettings(next)
    // Turning taking work on for the first time also opens the app with the computer, unless the person chose already.
    if (patch.enabled && !autostartChosen()) void chooseAutostart(true)
  }

  const [autostart, setAutostart] = useState(false)
  const [autostartBusy, setAutostartBusy] = useState(false)
  useEffect(() => {
    void autostartEnabled().then(setAutostart).catch(() => {})
  }, [])
  const chooseAutostart = async (on: boolean) => {
    setAutostartBusy(true)
    try {
      setAutostart(await autostartSet(on))
      try {
        localStorage.setItem(AUTOSTART_CHOSEN, '1')
      } catch {
        // Remembering the choice is a nicety.
      }
    } catch {
      // The system refused: the box shows what is really set.
      setAutostart(await autostartEnabled().catch(() => false))
    } finally {
      setAutostartBusy(false)
    }
  }

  // What the person changes here stands: the app does not tick back or switch back on by itself.
  const toggleRepository = (name: string) => {
    const excluded = settings.excluded ?? []
    update(
      settings.repositories.includes(name)
        ? { repositories: settings.repositories.filter((r) => r !== name), excluded: [...excluded.filter((r) => r !== name), name] }
        : { repositories: [...settings.repositories, name], excluded: excluded.filter((r) => r !== name) },
    )
  }

  return (
    <Panel title={t('connect.runnerTitle')}>
      <div className="stack">
        <p className="small muted" style={{ margin: 0 }}>{t('connect.runnerBody')}</p>

        {withFolder.length === 0 ? (
          <p className="small" style={{ margin: 0 }}>
            {t('connect.noFolders')} <Link to="/agents/repositories">{t('repos.link')}</Link>
          </p>
        ) : (
          <div className="stack" style={{ gap: 'var(--s1)' }}>
            <span className="field__label">{t('connect.runnerRepos')}</span>
            {withFolder.map((repository) => (
              <label key={repository.id} className="row row--tight small">
                <input type="checkbox" checked={settings.repositories.includes(repository.name)} onChange={() => toggleRepository(repository.name)} />
                <span className="mono">{repository.name}</span>
                <span className="tiny muted mono truncate">{folderFor(repository.name)}</span>
              </label>
            ))}
          </div>
        )}

        <div className="row row--wrap">
          <label className="field" style={{ minWidth: 200 }}>
            <span className="field__label">{t('connect.runnerAgent')}</span>
            <select className="select" value={settings.agent} onChange={(e) => update({ agent: e.target.value as LocalAgentId })}>
              {runnable.map((agent) => (
                <option key={agent.id} value={agent.id}>{agent.name}</option>
              ))}
            </select>
          </label>
          <label className="field" style={{ minWidth: 260 }}>
            <span className="field__label">{t('connect.permission')}</span>
            {/* The web learns the level with the next inventory report (docs/refactor/59): told in a moment, not in ten minutes. */}
            <select className="select" value={settings.permission} onChange={(e) => { update({ permission: e.target.value as Permission }); inventoryChanged() }}>
              <option value="edits">{t('connect.permission.edits')}</option>
              <option value="all">{t('connect.permission.all')}</option>
              {/* "O computador inteiro" (the owner, 28/10/2026; docs/refactor/59): the agent may leave the project. */}
              <option value="computer">{t('connect.permission.computer')}</option>
            </select>
          </label>
        </div>
        {settings.permission === 'all' && <div className="notice notice--warn small">{t('connect.permissionWarning')}</div>}
        {settings.permission === 'computer' && <div className="notice notice--warn small">{t('connect.permissionWarningComputer')}</div>}
        <FailoverOrder
          main={settings.agent}
          available={runnable.map((a) => ({ id: a.id, name: a.name }))}
          value={settings.failover ?? []}
          onChange={(failover) => update({ failover })}
        />

        {/* The home computer that takes work does not sleep, and comes back by itself after a restart (docs/refactor/28). */}
        <div className="stack" style={{ gap: 'var(--s1)' }}>
          <label className="row row--tight small">
            <input type="checkbox" checked={settings.keepAwake !== false} onChange={(e) => update({ keepAwake: e.target.checked })} />
            <span>{t('connect.keepAwake')}</span>
          </label>
          <label className="row row--tight small">
            <input type="checkbox" checked={autostart} disabled={autostartBusy} onChange={(e) => void chooseAutostart(e.target.checked)} />
            <span>{t('connect.autostart')}</span>
          </label>
        </div>

        <div className="row row--tight">
          <button
            type="button"
            className={`btn ${settings.enabled ? '' : 'btn--primary'}`}
            onClick={() => update({ enabled: !settings.enabled, optedOut: settings.enabled })}
            disabled={!settings.enabled && (settings.repositories.length === 0 || runnable.length === 0)}
          >
            <Power size={14} /> {settings.enabled ? t('connect.runnerOff') : t('connect.runnerOn')}
          </button>
          {settings.enabled && <Badge tone="ok">{t('connect.runnerIsOn')}</Badge>}
        </div>

        {status.filter((repo) => (repo.lane ?? 1) === 1).map((repo) => (
          <div key={repo.repository} className="stack runner-status" style={{ gap: 4 }}>
            <div className="row row--tight">
              <TerminalSquare size={13} />
              <span className="small mono">{repo.repository}</span>
              <Badge tone={repo.state === 'working' ? 'accent' : repo.state === 'listening' ? 'ok' : repo.state === 'idle' ? 'neutral' : 'warn'}>
                {t(`connect.state.${repo.state}`)}
              </Badge>
            </div>
            {repo.log.length > 0 && <pre className="runner-log mono tiny">{repo.log.slice(-12).join('\n')}</pre>}
          </div>
        ))}
      </div>
    </Panel>
  )
}
